import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Markdown } from '@shared/components/typography/Markdown/Markdown';
import {
  buildAddOnDisplayData,
  buildHardwareDisplayData,
  categorizeChildren,
  getRecurringPrice,
  getSubItemPromo,
  inferServiceType,
  parseOfferingDescription,
} from '@components/ProductOffers/modals/ShoppingCartDrawer/utils/cartItemCard.utils';
import { DiscountInfo } from '@shared/components/display/DiscountInfo/DiscountInfo';
import { PromoPriceDisplay } from '@shared/components/display/PromoPriceDisplay/PromoPriceDisplay';
import CartAddOnsContent from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartAddOnsContent';
import CartHardwareContent from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartHardwareContent';
import type { CartItemCardProps } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartItemCard.types';
import {
  ActionLinksRow,
  CartItemCardWrapper,
  ExpandableFooter,
  ExpandButton,
  VerticalDivider,
} from '@components/ProductOffers/modals/ShoppingCartDrawer/ShoppingCartDrawer.styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const CartItemCard = ({
  item,
  plan,
  equipmentList = [],
  onViewDetails,
  onEdit,
  onDelete,
  onUpdateChildQuantity,
}: CartItemCardProps) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  const price = getRecurringPrice(item);
  const promo = getSubItemPromo(item);
  const { discountTag, discountMessage } = parseOfferingDescription(item.productOffering?.description);
  const serviceType = inferServiceType(item, plan);
  const highlights = plan?.description?.highlights ?? [];
  const childItems = item.childItems ?? [];
  const planAddOns = plan?.addOns ?? [];

  const categorized = useMemo(
    () => categorizeChildren(childItems, planAddOns, plan?.typeName),
    [childItems, planAddOns, plan?.typeName]
  );

  // For bundles, also surface equipment nested under mandatory member children
  const memberEquipment = useMemo(
    () =>
      categorized.mandatoryMembers.flatMap((member) =>
        (member.childItems ?? []).filter((c) => c.type === 'equipment')
      ),
    [categorized.mandatoryMembers]
  );
  const allProducts = [...categorized.products, ...memberEquipment];

  const hasAddOns =
    categorized.addOns.length > 0 || categorized.channels.length > 0 || categorized.themePackages.length > 0;
  const hasHardware = allProducts.length > 0;
  const hasBundleMembers = categorized.mandatoryMembers.length > 0;
  const hasSections = hasAddOns || hasHardware || hasBundleMembers;

  const addOnDisplay = useMemo(() => buildAddOnDisplayData(categorized, planAddOns), [categorized, planAddOns]);

  const hardwareDisplay = useMemo(
    () => buildHardwareDisplayData(allProducts, equipmentList),
    [allProducts, equipmentList]
  );

  const isAddOnsExpanded = expanded === 'addOns';
  const isEquipmentExpanded = expanded === 'equipment';

  const toggleSection = (panel: string) => () => {
    setExpanded((prev) => (prev === panel ? false : panel));
  };

  return (
    <CartItemCardWrapper>
      {/* Card body: side-by-side on tablet+, stacked into a column on mobile so
          the highlights stop wrapping one word per line in the narrow drawer. */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        spacing={2.5}
        useFlexGap
        sx={{ p: 2.5 }}
      >
        {/* Plan info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Typography variant="label.button">{item.productOffering?.name ?? 'Unknown Plan'}</Typography>
            {serviceType && <Typography variant="subtitle.card">{serviceType}</Typography>}
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {highlights.map((h, i) => (
              <Typography key={i} variant="body.sm">
                <Markdown text={h} />
              </Typography>
            ))}
          </Box>
        </Box>

        {/* Price + promo badge: right-aligned beside the info on tablet+,
            left-aligned beneath it when stacked. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'flex-end' },
            gap: '4px',
          }}
        >
          <PromoPriceDisplay regularPrice={price} />
          <DiscountInfo promo={promo} discountTag={discountTag} discountMessage={discountMessage} />
        </Box>
      </Stack>

      {/* Action links */}
      <ActionLinksRow sx={{ pb: hasSections ? 0 : '20px' }}>
        <Typography variant="link.action" onClick={onViewDetails}>
          View Plan Details
        </Typography>
        {onEdit && (
          <Typography variant="link.action" onClick={onEdit}>
            Edit
          </Typography>
        )}
        {onDelete && (
          <Typography variant="link.action" onClick={onDelete}>
            Delete
          </Typography>
        )}
      </ActionLinksRow>

      {/* Expandable footer buttons */}
      {hasSections && (
        <ExpandableFooter>
          {hasBundleMembers && (
            <ExpandButton
              onClick={toggleSection('bundleMembers')}
              endIcon={expanded === 'bundleMembers' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              View Includes
            </ExpandButton>
          )}
          {hasBundleMembers && (hasAddOns || hasHardware) && <VerticalDivider />}
          {hasAddOns && (
            <ExpandButton
              onClick={toggleSection('addOns')}
              endIcon={isAddOnsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              View Add-Ons
            </ExpandButton>
          )}
          {hasAddOns && hasHardware && <VerticalDivider />}
          {hasHardware && (
            <ExpandButton
              onClick={toggleSection('equipment')}
              endIcon={isEquipmentExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              View Equipment
            </ExpandButton>
          )}
        </ExpandableFooter>
      )}

      {/* Collapsible bundle members */}
      {hasBundleMembers && (
        <Collapse in={expanded === 'bundleMembers'}>
          <Box sx={{ px: 2.5, pb: 2, display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {categorized.mandatoryMembers.map((member, i) => (
              <Typography key={i} variant="body.sm">
                &bull;&nbsp;{member.productOffering?.name ?? member.name ?? 'Included Item'}
              </Typography>
            ))}
          </Box>
        </Collapse>
      )}

      {/* Collapsible add-ons content */}
      {hasAddOns && (
        <Collapse in={isAddOnsExpanded}>
          <CartAddOnsContent
            themePackages={addOnDisplay.themePackages}
            individualChannels={addOnDisplay.individualChannels}
            serviceAddOns={addOnDisplay.serviceAddOns}
            onUpdateChildQuantity={onUpdateChildQuantity}
          />
        </Collapse>
      )}

      {/* Collapsible equipment content */}
      {hasHardware && (
        <Collapse in={isEquipmentExpanded}>
          <CartHardwareContent items={hardwareDisplay} onUpdateChildQuantity={onUpdateChildQuantity} />
        </Collapse>
      )}
    </CartItemCardWrapper>
  );
};
